/**
 * MUTAGIM.SHOP — Admin Email Queue Page (Phase 9)
 * Visualisation de la queue d'emails
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, ArrowLeft, Clock, CheckCircle, XCircle, 
  AlertCircle, Loader2, RefreshCw, Trash2, RotateCcw
} from 'lucide-react';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminEmailQueuePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState(null);
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchQueue();
    fetchStats();
  }, [filter]);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/admin/emails/queue`);
      if (filter) url.searchParams.set('status', filter);
      url.searchParams.set('limit', '50');

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails || []);
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/queue/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const cancelEmail = async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/queue/${emailId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Email annulé');
        fetchQueue();
        fetchStats();
      }
    } catch (error) {
      console.error('Error cancelling email:', error);
      toast.error('Erreur d\'annulation');
    }
  };

  const retryEmail = async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/queue/${emailId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Email remis en queue');
        fetchQueue();
        fetchStats();
      }
    } catch (error) {
      console.error('Error retrying email:', error);
      toast.error('Erreur');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'queued': return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'sent': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'logged': return <CheckCircle className="w-4 h-4 text-blue-400" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-gray-200" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-200" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'queued': return 'En attente';
      case 'sent': return 'Envoyé';
      case 'logged': return 'Loggé';
      case 'failed': return 'Échoué';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminPageWrapper title="Queue d'emails" subtitle="Visualisation de la file d'attente">
      <div className="space-y-6" data-testid="admin-email-queue-page">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { key: null, label: 'Total', value: stats.total || 0, color: 'text-white' },
            { key: 'queued', label: 'En attente', value: stats.queued || 0, color: 'text-yellow-400' },
            { key: 'logged', label: 'Loggés', value: stats.logged || 0, color: 'text-blue-400' },
            { key: 'sent', label: 'Envoyés', value: stats.sent || 0, color: 'text-green-400' },
            { key: 'failed', label: 'Échoués', value: stats.failed || 0, color: 'text-red-400' },
          ].map(stat => (
            <button
              key={stat.label}
              onClick={() => setFilter(stat.key)}
              className={cn(
                "p-4 rounded-lg border transition-colors text-left",
                filter === stat.key 
                  ? "bg-gray-800 border-mutagim-red" 
                  : "bg-gray-900 border-gray-800 hover:border-gray-700"
              )}
            >
              <p className="text-gray-200 text-sm">{stat.label}</p>
              <p className={cn("text-2xl font-bold", stat.color)}>{stat.value}</p>
            </button>
          ))}
        </div>

        {/* Mode LOG Banner */}
        <div className="mb-6 p-4 bg-blue-900/30 border border-blue-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400" />
          <div>
            <p className="text-white font-medium">Mode LOG actif</p>
            <p className="text-sm text-gray-200">
              Les emails sont enregistrés mais non envoyés. Activez Resend dans les paramètres pour l'envoi réel.
            </p>
          </div>
        </div>

        {/* Liste des emails */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-16">
            <Mail className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <p className="text-gray-200">Aucun email dans la queue</p>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Template</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Destinataire</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Sujet</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Date</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {emails.map((email) => (
                  <tr key={email.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(email.status)}
                        <span className="text-sm text-white">{getStatusLabel(email.status)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white">{email.template_key}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-white">{email.recipient_name}</p>
                        <p className="text-xs text-gray-200">{email.recipient_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white truncate max-w-[200px] block">
                        {email.subject}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-200">{formatDate(email.created_at)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {email.status === 'queued' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cancelEmail(email.id)}
                          className="text-gray-200 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {email.status === 'failed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => retryEmail(email.id)}
                          className="text-gray-200 hover:text-green-400"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminPageWrapper>
  );
}

export default AdminEmailQueuePage;
